/**
 * Yanamas mission flyleaf — the opening page of the diary theme.
 *
 * When the diary theme is on, the product is "yanamas" (previously
 * horton.io) and this card is where that identity and its mission live:
 * a book's flyleaf, sitting above the day's content. Dismissible, and it
 * stays dismissed (localStorage) so it never becomes a nag.
 */
import React, { useState } from 'react'
import { X } from 'lucide-react'
import { useTheme } from '@/components/providers/ThemeProvider'
import { DIARY_FLAVOUR } from '@/hooks/useBranding'
import { BRAND } from '@/config/brand'

const DISMISS_KEY = 'yanamas-flyleaf-dismissed'

interface YanamasFlyleafProps {
  /** Gallery/showcase use: render even if the user dismissed it. */
  alwaysShow?: boolean
  className?: string
}

export const YanamasFlyleaf: React.FC<YanamasFlyleafProps> = ({
  alwaysShow = false,
  className = '',
}) => {
  const { isDiary } = useTheme()
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  )

  if (!isDiary || (dismissed && !alwaysShow)) return null

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1')
    setDismissed(true)
  }

  return (
    <div className={`yn-flyleaf p-5 sm:p-6 ${className}`}>
      {/* corner sparkles */}
      <span className="yn-flyleaf-sparkle" style={{ top: 10, left: 14 }}>✦</span>
      <span className="yn-flyleaf-sparkle" style={{ top: 22, right: 40, animationDelay: '-0.9s' }}>✧</span>
      <span className="yn-flyleaf-sparkle" style={{ bottom: 12, left: '38%', animationDelay: '-1.7s' }}>✦</span>

      {!alwaysShow && (
        <button
          onClick={dismiss}
          aria-label="Tuck the flyleaf away"
          title="Tuck this page away"
          className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-black/5"
          style={{ color: 'var(--ink-faded)' }}
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <div className="flex flex-col items-center text-center gap-2">
        <div className="text-3xl" aria-hidden>🐘</div>
        <div className="yn-flyleaf-wordmark text-4xl sm:text-5xl">{BRAND.name}</div>
        <p className="yn-flyleaf-mission text-sm sm:text-base max-w-xl mt-2">
          “{BRAND.tagline}” — {DIARY_FLAVOUR.mission}
        </p>
        <div className="flex flex-wrap justify-center gap-2 mt-3">
          {DIARY_FLAVOUR.pillars.map((p: { emoji: string; label: string }) => (
            <span key={p.label} className="yn-flyleaf-pillar px-3 py-1 text-xs">
              <span className="mr-1" aria-hidden>{p.emoji}</span>
              {p.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

export default YanamasFlyleaf
